import Phaser from 'phaser';
import { CampManager } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { CraftingManager } from '../systems/CraftingManager';
import { UpgradeManager } from '../systems/UpgradeManager';
import { ResourceManager, ResourceStock } from '../systems/ResourceManager';
import { SfxManager } from '../utils/SfxManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { ResourceBanner } from '../ui/ResourceBanner';
import { CraftRecipe } from '../data/crafting';
import { BUILDING_DEFINITIONS, BUILDING_ORDER, BuildingId } from '../data/buildings';
import { ITEM_DEFINITIONS, CONSUMABLE_DEFINITIONS } from '../data/items';
import { showFloatingText } from '../utils/FloatingText';
import { addResourceCostLine } from '../utils/IconHelper';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { TutorialOverlay } from '../ui/TutorialOverlay';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { IslandCampFlag } from '../systems/IslandCampFlag';

const ATELIER_TUTO_KEY = 'deadtide_atelier_tuto_done';

// === SCÈNE DE L'ATELIER ===
// Deux onglets : Craft (recettes) et Bâtiments (upgrades).

type AtelierTab = 'craft' | 'upgrades';

const HEADER_H = 95;

const RARITY_COLORS: Record<string, string> = {
  common: '#bdc3c7',
  uncommon: '#2ecc71',
  rare: '#f1c40f',
};

export class AtelierScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private camp!: CampManager;
  private meta!: MetaManager;
  private upgrades!: UpgradeManager;
  private resources!: ResourceManager;
  private crafting!: CraftingManager;
  private sfx = new SfxManager();
  private currentTab: AtelierTab = 'craft';
  private banner!: ResourceBanner;

  constructor() {
    super({ key: 'AtelierScene' });
  }

  init(data: { camp?: CampManager; meta?: MetaManager }): void {
    this.camp = data?.camp ?? new CampManager();
    this.meta = data?.meta ?? new MetaManager();
    this.upgrades = this.camp.getUpgrades();
    this.resources = this.camp.getResources();
    this.crafting = new CraftingManager(this.resources, this.upgrades);
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
    this.showTutorialIfNeeded();
  }

  private showTutorialIfNeeded(): void {
    let done = false;
    try { done = !!localStorage.getItem(ATELIER_TUTO_KEY); } catch { /* */ }
    if (done) return;

    const tuto = new TutorialOverlay(this, [
      {
        text: '🔨 L\'Atelier a deux onglets :\n⚒️ Craft pour fabriquer des objets\n🏗️ Bâtiments pour améliorer le camp.',
        x: GAME_CX, y: 200,
        arrowTo: { x: GAME_CX, y: 95 },
      },
      {
        text: '⚒️ Craft — Chaque recette affiche\nses coûts en ressources (🪵🔩🧵💎☠️).\nVérifiez le bandeau en haut\npour voir vos stocks.',
        x: GAME_CX, y: 200,
        arrowTo: { x: GAME_CX, y: 25 },
      },
      {
        text: '🏗️ Bâtiments — Améliorez le Dortoir,\nle Coffre, l\'Atelier, la Vigie et l\'Autel.\nChaque niveau débloque de nouvelles\nfonctionnalités et capacités.',
        x: GAME_CX, y: 300,
      },
    ], () => {
      try { localStorage.setItem(ATELIER_TUTO_KEY, '1'); } catch { /* */ }
    });
    tuto.start();
  }

  private drawUI(): void {
    this.children.removeAll(true);

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // Bandeau ressources
    this.banner = new ResourceBanner(this, this.resources);

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    this.add.text(GAME_CX, 15, '🔨 ATELIER', {
      fontSize: '20px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    const workshopLvl = this.upgrades.getBuildingLevel('workshop');
    this.add.text(GAME_CX, 40, `Niveau ${workshopLvl} — ${BUILDING_DEFINITIONS.workshop.levels[workshopLvl].label}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Construction en cours
    const construction = this.upgrades.getConstruction();
    if (construction) {
      const cDef = BUILDING_DEFINITIONS[construction.buildingId];
      this.add.text(GAME_CX, 55, `🏗️ En construction : ${cDef.emoji} ${cDef.name} → Niv.${construction.targetLevel} (fin au retour de run)`, {
        fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
      }).setOrigin(0.5).setDepth(11);
    }

    // Retour camp
    const backBtn = this.add.text(15, 10, '← Camp', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, IslandCampFlag.campSceneKey(), { camp: this.camp, meta: this.meta }));
    backBtn.on('pointerover', () => backBtn.setColor('#ff6b6b'));
    backBtn.on('pointerout', () => backBtn.setColor('#e74c3c'));

    // === ONGLETS ===
    // Indicateur "!" si un upgrade est disponible et affordable
    const hasUpgradeAvailable = BUILDING_ORDER.some(id => {
      const cost = this.upgrades.getUpgradeCost(id);
      return cost && this.resources.canAfford(cost) && !this.upgrades.isBuilding();
    });
    const upgradeLabel = hasUpgradeAvailable ? '🏗️ Bâtiments ❗' : '🏗️ Bâtiments';
    // Sur mobile (450 px), on rapproche les ratios à 0.25/0.75 et on réduit le padding
    // pour que les onglets larges ("🏗️ Bâtiments ❗") ne se chevauchent pas.
    const _mobAtelier = GAME_W < 500;
    const tab1X = _mobAtelier ? Math.round(GAME_W * 0.27) : Math.round(GAME_W * 0.33);
    const tab2X = _mobAtelier ? Math.round(GAME_W * 0.73) : Math.round(GAME_W * 0.73);
    this.drawTab(tab1X, 75, '⚒️ Craft', 'craft');
    this.drawTab(tab2X, 75, upgradeLabel, 'upgrades');

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H - 24);

    let y = 10;
    if (this.currentTab === 'craft') {
      y = this.drawCraftTab(y);
    } else {
      y = this.drawUpgradesTab(y);
    }

    y += 30;
    this.panel.setContentHeight(y);
  }

  // === ONGLETS ===

  private drawTab(x: number, y: number, label: string, tab: AtelierTab): void {
    const active = this.currentTab === tab;
    const _mobTab = GAME_W < 500;
    const btn = this.add.text(x, y, label, {
      fontSize: _mobTab ? '12px' : '14px',
      color: active ? '#f1c40f' : '#8899aa',
      fontFamily: FONT_BODY,
      fontStyle: 'bold',
      backgroundColor: active ? '#1a2a40' : '#151525',
      padding: { x: _mobTab ? 10 : 24, y: 8 },
    }).setOrigin(0.5).setDepth(11);
    if (active) {
      // Soulignement de l'onglet actif
      this.add.rectangle(x, y + 18, btn.width, 3, 0xf1c40f).setDepth(11);
    }

    if (!active) {
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.sfx.select();
        this.currentTab = tab;
        this.drawUI();
      });
      btn.on('pointerover', () => btn.setColor('#bdc3c7'));
      btn.on('pointerout', () => btn.setColor('#7f8c8d'));
    }
  }

  // === ONGLET CRAFT ===

  private drawCraftTab(y: number): number {
    const recipes = this.crafting.getAvailableRecipes();
    const workshopLvl = this.upgrades.getBuildingLevel('workshop');
    const rowW = Math.min(560, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);

    if (recipes.length === 0) {
      this.panel.add(this.add.text(GAME_CX, y + 30, 'Aucune recette disponible.\nAméliorez l\'atelier pour débloquer le craft.', {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY, align: 'center',
      }).setOrigin(0.5));
      return y + 80;
    }

    // Grouper par tier
    for (let tier = 1; tier <= workshopLvl; tier++) {
      const tierRecipes = recipes.filter(r => r.workshopLevel === tier);
      if (tierRecipes.length === 0) continue;

      const tierLabel = tier === 1 ? 'Basiques' : tier === 2 ? 'Avancées' : 'Légendaires';
      this.panel.add(this.add.text(lx, y, `--- Recettes ${tierLabel} (Niv.${tier}) ---`, {
        fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
      }));
      y += 22;

      for (const recipe of tierRecipes) {
        y = this.drawRecipeRow(recipe, y);
        y += 8;
      }
      y += 5;
    }

    return y;
  }

  private drawRecipeRow(recipe: CraftRecipe, y: number): number {
    const rowW = Math.min(560, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const rx = Math.round(GAME_CX + rowW / 2 - 4);
    const costX = Math.round(GAME_CX - rowW * 0.08); // milieu-droit pour le coût
    const canCraft = this.crafting.canCraft(recipe.id);

    // Déterminer la couleur selon le type de résultat
    let rarityColor = '#bdc3c7';
    if (recipe.resultType === 'item') {
      const def = ITEM_DEFINITIONS[recipe.resultId];
      if (def) rarityColor = RARITY_COLORS[def.rarity] ?? '#bdc3c7';
    } else {
      const def = CONSUMABLE_DEFINITIONS[recipe.resultId];
      if (def) rarityColor = RARITY_COLORS[def.rarity] ?? '#bdc3c7';
    }

    // Fond
    const bg = this.add.rectangle(GAME_CX, y + 18, rowW, 40, canCraft ? 0x1e2d4a : 0x151520);
    bg.setStrokeStyle(1, canCraft ? 0x2a3a5c : 0x1a1a2a);
    this.panel.add(bg);

    // Nom + emoji
    this.panel.add(this.add.text(lx, y + 5, `${recipe.emoji} ${recipe.name}`, {
      fontSize: '12px', color: canCraft ? rarityColor : '#6c7a89', fontFamily: FONT_BODY,
    }));

    // Description
    this.panel.add(this.add.text(lx, y + 22, recipe.description, {
      fontSize: '10px', color: canCraft ? '#7f8c8d' : '#6c7a89', fontFamily: FONT_BODY,
    }));

    // Coût avec icônes SVG (maxWidth = du costX au bouton droit, avec marge)
    const stock = this.resources.getAll();
    const recipeCostMaxWidth = Math.max(140, rx - costX - 80);
    const costObjs = addResourceCostLine(this, costX, y + 12, recipe.cost, stock, {
      fontSize: '10px', maxWidth: recipeCostMaxWidth,
    });
    costObjs.forEach(obj => this.panel.add(obj));

    // Bouton crafter
    if (canCraft) {
      const btn = this.add.text(rx, y + 26, '[Crafter]', {
        fontSize: '10px', color: '#2ecc71', fontFamily: FONT_BODY,
        backgroundColor: '#1a2a1a', padding: { x: 4, y: 3 },
      }).setOrigin(1, 0);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => this.doCraft(recipe));
      btn.on('pointerover', () => btn.setColor('#27ae60'));
      btn.on('pointerout', () => btn.setColor('#2ecc71'));
      this.panel.add(btn);
    } else {
      this.panel.add(this.add.text(rx, y + 26, 'Manque', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(1, 0));
    }

    return y + 40;
  }

  private doCraft(recipe: CraftRecipe): void {
    const result = this.crafting.craft(recipe.id);
    if (!result) return;

    this.sfx.craft();
    showFloatingText(this, 400, 300, `✨ ${recipe.emoji} ${recipe.name} crafté !`, '#2ecc71');

    // Ajouter le résultat au coffre
    if (result.resultType === 'item') {
      const added = this.camp.addItemToStorage(result.resultId);
      if (!added) {
        // Coffre plein — on rembourse
        this.resources.addMultiple(result.cost);
        return;
      }
    } else {
      // Consommable : stocké dans la réserve de consommables du camp
      this.camp.addConsumable(result.resultId);
    }

    this.drawUI();
  }

  // === ONGLET BÂTIMENTS ===

  private drawUpgradesTab(y: number): number {
    const construction = this.upgrades.getConstruction();

    for (const buildingId of BUILDING_ORDER) {
      y = this.drawBuildingRow(buildingId, construction, y);
      y += 10;
    }

    return y;
  }

  private drawBuildingRow(id: BuildingId, construction: ReturnType<UpgradeManager['getConstruction']>, y: number): number {
    const rowW = Math.min(560, GAME_W - 10);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const rx = Math.round(GAME_CX + rowW / 2 - 4);
    const midR = Math.round(GAME_CX + rowW * 0.12); // colonne info droite
    const def = BUILDING_DEFINITIONS[id];
    const level = this.upgrades.getBuildingLevel(id);
    const maxLevel = def.levels.length - 1;
    const isMaxed = level >= maxLevel;
    const isBuilding = construction?.buildingId === id;
    const hasConstruction = construction !== null;

    // Fond
    const bg = this.add.rectangle(GAME_CX, y + 28, rowW, 60, isBuilding ? 0x2a2a1a : 0x1e2d4a);
    bg.setStrokeStyle(1, isBuilding ? 0xe67e22 : 0x2a3a5c);
    this.panel.add(bg);

    // Nom + niveau
    const levelStars = '★'.repeat(level) + '☆'.repeat(maxLevel - level);
    this.panel.add(this.add.text(lx, y + 8, `${def.emoji} ${def.name}  ${levelStars}`, {
      fontSize: '13px', color: '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'bold',
    }));

    // Description du niveau actuel
    this.panel.add(this.add.text(lx, y + 26, def.levels[level].label, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }));

    // État / bouton upgrade
    if (isBuilding) {
      this.panel.add(this.add.text(midR, y + 15, '🏗️ En cours', {
        fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
      }));
      this.panel.add(this.add.text(midR, y + 32, 'Fin au retour', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }));
    } else if (isMaxed) {
      this.panel.add(this.add.text(rx, y + 22, 'MAX', {
        fontSize: '12px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
      }).setOrigin(1, 0.5));
    } else {
      const cost = this.upgrades.getUpgradeCost(id);
      const canAfford = cost ? this.resources.canAfford(cost) : false;
      const canBuild = canAfford && !hasConstruction;

      // Coût du prochain niveau
      const nextLabel = def.levels[level + 1].label;
      this.panel.add(this.add.text(midR, y + 8, `→ Niv.${level + 1}: ${nextLabel}`, {
        fontSize: '10px', color: canBuild ? '#bdc3c7' : '#7f8c8d', fontFamily: FONT_BODY,
        wordWrap: { width: rowW * 0.38 },
      }));

      if (cost) {
        const stock = this.resources.getAll();
        // Largeur disponible : du midR au bouton à droite (rx) - marge bouton
        const costMaxWidth = Math.max(140, rx - midR - 80);
        const costObjs = addResourceCostLine(this, midR, y + 28, cost, stock, {
          fontSize: '10px', maxWidth: costMaxWidth,
        });
        costObjs.forEach(obj => this.panel.add(obj));
      }

      if (canBuild) {
        const btn = this.add.text(rx, y + 36, '[Construire]', {
          fontSize: '10px', color: '#2ecc71', fontFamily: FONT_BODY,
          backgroundColor: '#1a2a1a', padding: { x: 4, y: 2 },
        }).setOrigin(1, 0);
        btn.setInteractive({ useHandCursor: true });
        btn.on('pointerdown', () => this.doUpgrade(id));
        btn.on('pointerover', () => btn.setColor('#27ae60'));
        btn.on('pointerout', () => btn.setColor('#2ecc71'));
        this.panel.add(btn);
      } else if (hasConstruction) {
        this.panel.add(this.add.text(rx, y + 36, 'File occupée', {
          fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        }).setOrigin(1, 0));
      } else {
        this.panel.add(this.add.text(rx, y + 36, 'Manque', {
          fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        }).setOrigin(1, 0));
      }
    }

    return y + 60;
  }

  shutdown(): void {
    this.panel?.destroy();
  }

  private doUpgrade(id: BuildingId): void {
    const ok = this.upgrades.startConstruction(id, this.resources);
    if (!ok) return;
    this.sfx.build();
    const def = BUILDING_DEFINITIONS[id];
    showFloatingText(this, 400, 300, `🏗️ ${def.emoji} ${def.name} en construction !`, '#e67e22');
    this.drawUI();
  }

}
